import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetProjectDeliverableById,
  useUpdateProjectDeliverableMutation,
} from "../../../../../../../../../../api/services/project/projectApis";
import { InformationBadge } from "../../../../../../../../../../common/informationBadge/informationBadge";
import {
  MuiDoubleLabel,
  MuiGrid,
  MuiLink,
  MuiModal,
  WarningConfirmationModal,
} from "../../../../../../../../../../components";
import { ViewManageLocale } from "../../../../../../../../../../components/manageLocale/ViewManageLocale";
import { UnitTypesKey } from "../../../../../../../../../../helpers/constants/siteCoreKeys";
import {
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { ProjectViewDeliverablesWrapper } from "./ProjectViewDeliverables.style";
import { addDeliverableSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import MeasurementInput from "../../../../../../../../../../components/measurementInput/MeasurementInput";
import {
  ProjectUpdateDeliverablesFormKeys,
  UpdateDeliverablesFormProps,
} from "../../modelsAndSchemas/models";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { UpdateProjectDeliverableValidationSchema } from "../../modelsAndSchemas/ValidationSchema";
import { ProjectDeliverableParams } from "../../../../../../../../../../api/services/project/model";
import { keyDetailsSelector } from "../../../../../../../../../../redux/slices/project/projectSelector";
import { MuiDoubleLabelWithChip } from "../../../../../../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { LANGUAGE_CODE } from "../../../../../../../../../../helpers/constants";

interface ProjectViewDeliverablesProps {
  visible: boolean;
  onCloseModal: () => void;
  pageId: string;
  selectedProjectDeliverableId: string;
  isEditDeliverable?: boolean;
  onEditButtonClick?: () => void;
  callApiFunction?: () => void;
  hideEditButton?: boolean;
}

export const ProjectViewDeliverables: FC<ProjectViewDeliverablesProps> = ({
  visible,
  onCloseModal,
  pageId,
  selectedProjectDeliverableId,
  isEditDeliverable = false,
  onEditButtonClick,
  callApiFunction,
  hideEditButton,
}) => {
  const dispatch = useDispatch();
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const gridRef = useRef<HTMLDivElement>(null);
  const [showManageLocaleModal, setShowManageLocaleModal] = useState(false);

  const onClickShowManageLocale = () => {
    setShowManageLocaleModal(true);
  };

  const onClickCloseShowManageLocale = () => {
    setShowManageLocaleModal(false);
  };

  const {
    commonLabels,
    fetchLabel,
    fetchValueByKeyOfDropdown,
    commonValidationMessages,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const [showCancel, setShowCancel] = useState(false);

  // update deliverables in case of edit framework deliverable from project
  const {
    mutateAsync: updateDeliverableApi,
    isPending: isUpdatePending,
    isError: isUpdateError,
    isSuccess: isUpdateSuccess,
  } = useUpdateProjectDeliverableMutation();

  const {
    data: deliverableData,
    isFetching: deliverableDataFetching,
    isError: isDeliverableDataError,
    refetchWithParams: getDeliverableData,
    isSuccess: isDeliverableSuccess,
  } = useGetProjectDeliverableById();

  const defaultValues = {
    [ProjectUpdateDeliverablesFormKeys.units]:
      deliverableData?.units || UnitTypesKey.Minutes,
    [ProjectUpdateDeliverablesFormKeys.measurementMinute]: "",
    [ProjectUpdateDeliverablesFormKeys.measurementOther]: null,
  };

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { isDirty: isDirtyForm },
  } = useForm<UpdateDeliverablesFormProps>({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      UpdateProjectDeliverableValidationSchema(commonValidationMessages) as any,
    ),
  });

  useEffect(() => {
    if (isUpdateSuccess) {
      if (callApiFunction) {
        callApiFunction();
      }
      onCloseModal();
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    getDeliverableData(
      selectedProjectDeliverableId,
      getValueOrBlank(projectKeyDetails?.id),
    );
  }, []);

  const loading = isUpdatePending || deliverableDataFetching;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: loading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [loading]);

  const error = isUpdateError || isDeliverableDataError;

  useEffect(() => {
    if (isDeliverableSuccess && isEditDeliverable) {
      setValue(
        deliverableData.units === UnitTypesKey.Minutes
          ? ProjectUpdateDeliverablesFormKeys.measurementMinute
          : ProjectUpdateDeliverablesFormKeys.measurementOther,
        deliverableData?.measurement,
      );
      setValue(ProjectUpdateDeliverablesFormKeys.units, deliverableData?.units);
    }
  }, [deliverableData]);

  useEffect(() => {
    if (error) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [error]);

  const getLabel = (key: string) => {
    return fetchLabel(addDeliverableSiteCoreKeys.addDeliverable, key);
  };

  const getUnitText = () => {
    return fetchValueByKeyOfDropdown(
      addDeliverableSiteCoreKeys.addDeliverable,
      addDeliverableSiteCoreKeys.unitsList,
      deliverableData?.units ?? "",
    );
  };

  const getMeasurementValue = () => {
    if (!deliverableData?.units) {
      return "";
    }
    if (deliverableData.units === UnitTypesKey.Minutes) {
      return fetchValueByKeyOfDropdown(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.minutesList,
        deliverableData.measurement,
      );
    }
    return deliverableData.measurement;
  };

  const unitValue = fetchValueByKeyOfDropdown(
    addDeliverableSiteCoreKeys.addDeliverable,
    addDeliverableSiteCoreKeys.unitsList,
    deliverableData?.units ?? "",
  );

  const onSubmitClick = () => {
    const { measurementMinute, measurementOther } = getValues();

    let measurement: string;

    const units = deliverableData?.units;

    if (units === UnitTypesKey.Minutes) {
      measurement = measurementMinute;
    } else if (units === UnitTypesKey.Count || units === UnitTypesKey.Days) {
      measurement = (measurementOther as number).toString();
    } else {
      measurement = "1";
    }

    const params: ProjectDeliverableParams = {
      localizedDeliverableNames: isNotEmpty(
        deliverableData?.localizedDeliverableNames,
      )
        ? deliverableData?.localizedDeliverableNames
        : [
            {
              localeKey: LANGUAGE_CODE.EN_US,
              name: deliverableData?.name,
            },
          ],
      deliverableType: deliverableData?.deliverableType,
      units,
      measurements: measurement,
    };
    if (isNotEmpty(projectKeyDetails))
      updateDeliverableApi({
        params,
        projectId: projectKeyDetails.id,
        deliverableId: selectedProjectDeliverableId,
      });
  };

  const handleSecondaryButtonClick = () => {
    if (isEditDeliverable && isDirtyForm) {
      setShowCancel(true);
    } else {
      onCloseModal();
    }
  };

  const bannerInfoMessage = useMemo(() => {
    return fetchLabel(
      addDeliverableSiteCoreKeys.addDeliverable,
      deliverableData?.isProjectFrameworkDeliverable
        ? addDeliverableSiteCoreKeys.thisIsAFrameworkLevelDeliverable
        : addDeliverableSiteCoreKeys.thisIsAProjectSpecificDeliverable,
    );
  }, [deliverableData]);

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    onCloseModal();
  };

  const title = replaceHashWithValue(
    getLabel(
      isEditDeliverable
        ? addDeliverableSiteCoreKeys.editDeliverableHeading
        : addDeliverableSiteCoreKeys.viewDeliverableHeading,
    ),
    deliverableData?.name ?? "",
  );

  const closeModalHandler = () => setShowCancel(false);

  const onUpdateBtnClick = () => {
    if (isEditDeliverable) handleSubmit(onSubmitClick)();
  };

  const activityTypes = useMemo(() => {
    return Array.isArray(deliverableData?.activityType)
      ? deliverableData?.activityType.map((value: string) => ({
          value: value,
          label: value,
        }))
      : [];
  }, [deliverableData]);

  return (
    <>
      <MuiModal
        open={visible}
        onClose={onCloseModal}
        title={title}
        onSecondaryButtonClick={handleSecondaryButtonClick}
        tertiaryButtonTitle={
          !isEditDeliverable && !hideEditButton
            ? getLabel(addDeliverableSiteCoreKeys.editDeliverableButtonText)
            : ""
        }
        onTertiaryButtonClick={onEditButtonClick}
        primaryButtonTitle={isEditDeliverable ? commonLabels.update : ""}
        onPrimaryButtonClick={() => onUpdateBtnClick()}
        hasLargeContent
        stackButtonsOnMobile={true}
        secondaryButtonTitle={
          isEditDeliverable ? commonLabels.cancel : commonLabels.close
        }
      >
        <>
          <InformationBadge message={bannerInfoMessage} />
          <ProjectViewDeliverablesWrapper>
            <MuiGrid container data-testid={`${pageId}-view-deliverables`}>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  label={getLabel(addDeliverableSiteCoreKeys.deliverableType)}
                  value={fetchValueByKeyOfDropdown(
                    addDeliverableSiteCoreKeys.addDeliverable,
                    addDeliverableSiteCoreKeys.deliverableTypeList,
                    deliverableData?.deliverableType ?? "",
                  )}
                />
              </MuiGrid>

              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  label={getLabel(addDeliverableSiteCoreKeys.deliverableName)}
                  value={deliverableData?.name ?? ""}
                  isValueWithEllipsis
                  valueClasses="deliverable-value"
                />
                <MuiLink
                  className="view-locale-link"
                  onClick={onClickShowManageLocale}
                >
                  {commonLabels.locale}
                </MuiLink>
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabelWithChip
                  label={getLabel(addDeliverableSiteCoreKeys.activityType)}
                  replaceEmptyWithDash={!activityTypes.length}
                  value={activityTypes}
                  chipPopoverGridRef={gridRef}
                  chipPopoverHeaderLabel={getLabel(
                    addDeliverableSiteCoreKeys.activityType,
                  )}
                  showMoreARIALabel={commonARIALabel.showNMore}
                  removeChipNameARIALabel={commonARIALabel.removeChipName}
                  closePopoverARIALabel={commonARIALabel.closePopover}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  label={getLabel(addDeliverableSiteCoreKeys.units)}
                  value={unitValue}
                />
              </MuiGrid>

              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                {isEditDeliverable ? (
                  <MeasurementInput
                    selectedUnit={deliverableData?.units}
                    measurementOtherMinuteName={
                      ProjectUpdateDeliverablesFormKeys.measurementMinute
                    }
                    measurementOther={
                      ProjectUpdateDeliverablesFormKeys.measurementOther
                    }
                    control={control}
                    pageId={pageId}
                    parentKey={addDeliverableSiteCoreKeys.addDeliverable}
                    minutesLabelKey={addDeliverableSiteCoreKeys.minutes}
                    quantityLabelKey={addDeliverableSiteCoreKeys.quantity}
                    daysLabelKey={addDeliverableSiteCoreKeys.numberOfDays}
                    minuteListKey={addDeliverableSiteCoreKeys.minutesList}
                  />
                ) : (
                  <MuiDoubleLabel
                    label={getUnitText()}
                    value={getMeasurementValue()}
                  />
                )}
              </MuiGrid>
            </MuiGrid>

            {showCancel && (
              <WarningConfirmationModal
                show={showCancel}
                modalTitle={commonLabels.cancelChanges}
                secondaryButtonTitle={commonLabels.no}
                onClose={closeModalHandler}
                onPrimaryButtonClick={cancelConfirmHandler}
                labelContent={commonLabels.cancelButtonAlertText}
              />
            )}
          </ProjectViewDeliverablesWrapper>
        </>
      </MuiModal>
      {showManageLocaleModal && (
        <ViewManageLocale
          show={showManageLocaleModal}
          handleCloseModal={onClickCloseShowManageLocale}
          localeLanguages={deliverableData?.localizedDeliverableNames ?? []}
          title={title}
          bannerInfoMessage={bannerInfoMessage}
        />
      )}
    </>
  );
};
